/**
 * Integration Test Definitions for EcoleMinds API
 *
 * HOW TO ADD A NEW TEST:
 * 1. Import the API client function from '@/api/client' (or write a raw fetch).
 * 2. Add a TestCase object to the appropriate section below.
 * 3. Run the Integration Test UI at /dev/integration-test to verify.
 *
 * RULES:
 * - Every new endpoint MUST have a corresponding integration test.
 * - Tests should be deterministic and safe to run repeatedly.
 * - Use skip: true for tests that require specific seeded data or destructive actions.
 * - Group tests logically (Public, Auth, User, Trainer, Admin).
 */

import {
  getCourses,
  getCourse,
  getFeaturedCourse,
  getBanners,
  getFaq,
  getTestimonials,
  getCourseReviews,
  getCourseQuestions,
  getQuestionAnswers,
  validateCoupon,
  getPublicSettings,
  getPublicSettingsBulk,
  googleLogin,
  refreshAccessToken,
  logout,
  acceptInvitation,
  getMe,
  updateMe,
  getMyEnrollments,
  getMyPayments,
  getMyNotifications,
  getCourseProgress,
  trackProgress,
  getWishlist,
  addToWishlist,
  removeFromWishlist,
  createReview,
  markReviewHelpful,
  askQuestion,
  postAnswer,
  getCourseNotes,
  createNote,
  updateNote,
  deleteNote,
  getInbox,
  getSentMessages,
  getUnreadCount,
  getConversation,
  sendMessage,
  getTickets,
  getTicket,
  createTicket,
  replyToTicket,
  applyAsTrainer,
  getMyTrainerApplication,
  getCourseCoupons,
  createCoupon,
  deleteCoupon,
  getTrainerDashboard,
  getTrainerCourses,
  getTrainerStudents,
  getTrainerEarnings,
  getTrainerPayouts,
  requestPayout,
  captureLead,
  getAdminStats,
  getAdminUsers,
  updateUser,
  deleteUser,
  getAdminCourses,
  getAdminCourse,
  adminCreateCourse,
  adminUpdateCourse,
  adminDeleteCourse,
  getAdminPayments,
  updatePayment,
  getAdminEnrollments,
  getAdminLeads,
  updateLead,
  deleteLead,
  getCategories,
  createCategory,
  getTrainers,
  createTrainer,
  updateTrainer,
  getCms,
  updateCms,
  getTrainerApplications,
  reviewTrainerApplication,
  getPendingReviews,
  approveReview,
  deleteReview,
  updateTicket,
  getContentFlags,
  updateContentFlag,
  getAdminPayouts,
  updatePayout,
  updateSystemSettings,
  inviteAdmin,
  getAdminLogs,
  getAdminLogStats,
  clearAdminLogs,
  getCourseModules,
  createCourseModule,
  updateCourseModule,
  deleteCourseModule,
  getCourseFaq,
  createCourseFaq,
  updateCourseFaq,
  deleteCourseFaq,
  getMarketingOverview,
  getMarketingFunnel,
  getMarketingChannels,
  getMarketingCampaigns,
  getMarketingCoupons,
  getMarketingTrends,
  trackAbandonedCheckout,
  getAdminAbandonedCheckouts,
  recoverAbandonedCheckout,
  createEnrollment,
  getEnrollment,
  createPaymentOrder,
  verifyPayment,
  createTrainerCourse,
  updateTrainerCourse,
  deleteTrainerCourse,
  sendAnnouncement,
  mentorChat,
  getMentorConversations,
  getMentorConversation,
  updateMentorGoal,
  getMentorGapAnalysis,
} from '../../api/client';

export type TestStatus = 'idle' | 'running' | 'passed' | 'failed' | 'skipped';

export interface TestCase {
  id: string;
  name: string;
  description?: string;
  group: string;
  run: () => Promise<any>;
  /** Set to true if the test requires a valid auth token */
  requiresAuth?: boolean;
  /** Set to true if the test requires admin role */
  requiresAdmin?: boolean;
  /** Set to true if the test requires trainer role */
  requiresTrainer?: boolean;
  /** Set to true to skip this test by default */
  skip?: boolean;
  /** Reason for skipping */
  skipReason?: string;
}

export interface TestResult {
  id: string;
  status: TestStatus;
  error?: string;
  durationMs?: number;
  responsePreview?: string;
}

// ───────────────────────────────────────────────────────────────
// Helper to safely call an API and throw on error
// ───────────────────────────────────────────────────────────────
async function expectOk<T>(promise: Promise<T>, previewKey?: string): Promise<T> {
  const data = await promise;
  // Most of our API wrappers return { success, data, error }
  const d = data as any;
  if (d && d.success === false) {
    throw new Error(d.error || 'API returned success: false');
  }
  return data;
}

// ───────────────────────────────────────────────────────────────
// PUBLIC TESTS
// ───────────────────────────────────────────────────────────────
const publicTests: TestCase[] = [
  {
    id: 'pub-courses-list',
    name: 'GET /courses — List courses',
    group: 'Public',
    run: () => expectOk(getCourses()),
  },
  {
    id: 'pub-courses-featured',
    name: 'GET /courses/featured — Featured course',
    group: 'Public',
    run: () => expectOk(getFeaturedCourse()),
  },
  {
    id: 'pub-banners',
    name: 'GET /banners — Banners',
    group: 'Public',
    run: () => expectOk(getBanners()),
  },
  {
    id: 'pub-faq',
    name: 'GET /faq — FAQ list',
    group: 'Public',
    run: () => expectOk(getFaq()),
  },
  {
    id: 'pub-testimonials',
    name: 'GET /testimonials — Testimonials',
    group: 'Public',
    run: () => expectOk(getTestimonials()),
  },
  {
    id: 'pub-settings',
    name: 'GET /settings — Public settings',
    group: 'Public',
    run: () => expectOk(getPublicSettings()),
  },
  {
    id: 'pub-settings-bulk',
    name: 'GET /settings/bulk — Bulk settings',
    group: 'Public',
    run: () => expectOk(getPublicSettingsBulk(['site_name', 'contact_email'])),
  },
  {
    id: 'pub-coupon-validate',
    name: 'POST /coupons/validate — Validate coupon',
    group: 'Public',
    run: async () => {
      // This will likely return "invalid" but should still be a valid API response
      try {
        await validateCoupon('TESTCODE999', 1);
      } catch (e: any) {
        // If the error is "invalid coupon" that's expected behavior, not a system error
        if (e.message && e.message.toLowerCase().includes('invalid')) return;
        throw e;
      }
    },
  },
  {
    id: 'pub-lead-capture',
    name: 'POST /leads — Lead capture',
    group: 'Public',
    run: () =>
      expectOk(
        captureLead({
          name: 'Integration Test',
          email: `test-${Date.now()}@example.com`,
          phone: '9999999999',
          interest: 'AI',
        }),
      ),
  },
  {
    id: 'pub-client-logs',
    name: 'POST /client-logs — Client error logging',
    group: 'Public',
    run: async () => {
      const res = await fetch(`${(import.meta as any).env.VITE_API_BASE_URL}/client-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level: 'warn', message: 'Integration test log' }),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
    },
  },
];

// ───────────────────────────────────────────────────────────────
// AUTH TESTS
// ───────────────────────────────────────────────────────────────
const authTests: TestCase[] = [
  {
    id: 'auth-google-invalid',
    name: 'POST /auth/google — Rejects invalid token',
    group: 'Auth',
    run: async () => {
      try {
        await googleLogin('invalid-token', 'dev-device', 'web');
        throw new Error('Should have rejected invalid token');
      } catch (e: any) {
        if (e.message && e.message.toLowerCase().includes('invalid')) return;
        if (e.message && e.message.toLowerCase().includes('unauthorized')) return;
        throw e;
      }
    },
  },
  {
    id: 'auth-refresh-invalid',
    name: 'POST /auth/refresh — Rejects invalid refresh token',
    group: 'Auth',
    run: async () => {
      try {
        await refreshAccessToken('invalid-refresh-token');
        throw new Error('Should have rejected invalid token');
      } catch (e: any) {
        if (e.message && (e.message.includes('401') || e.message.toLowerCase().includes('invalid'))) return;
        throw e;
      }
    },
  },
  {
    id: 'auth-logout-invalid',
    name: 'POST /auth/logout — Handles invalid token gracefully',
    group: 'Auth',
    run: async () => {
      try {
        await logout('invalid-token');
      } catch {
        // logout may fail but should not crash the server
      }
    },
  },
];

// ───────────────────────────────────────────────────────────────
// PROTECTED USER TESTS
// ───────────────────────────────────────────────────────────────
const userTests: TestCase[] = [
  {
    id: 'user-me',
    name: 'GET /me — Current user profile',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMe()),
  },
  {
    id: 'user-me-update',
    name: 'PUT /me — Update profile',
    group: 'User',
    requiresAuth: true,
    run: () =>
      expectOk(
        updateMe({
          full_name: 'Integration Test User',
          phone: '9999999999',
        }),
      ),
  },
  {
    id: 'user-enrollments',
    name: 'GET /me/enrollments — My enrollments',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMyEnrollments()),
  },
  {
    id: 'user-payments',
    name: 'GET /me/payments — My payments',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMyPayments()),
  },
  {
    id: 'user-notifications',
    name: 'GET /me/notifications — Notifications',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMyNotifications()),
  },
  {
    id: 'user-wishlist',
    name: 'GET /wishlist — Wishlist',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getWishlist()),
  },
  {
    id: 'user-progress',
    name: 'GET /progress/courses/:id — Course progress',
    group: 'User',
    requiresAuth: true,
    skip: true,
    skipReason: 'Requires valid enrollment with course_id',
    run: () => expectOk(getCourseProgress(1)),
  },
  {
    id: 'user-inbox',
    name: 'GET /messages/inbox — Inbox',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getInbox()),
  },
  {
    id: 'user-sent',
    name: 'GET /messages/sent — Sent messages',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getSentMessages()),
  },
  {
    id: 'user-unread',
    name: 'GET /messages/unread-count — Unread count',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getUnreadCount()),
  },
  {
    id: 'user-tickets',
    name: 'GET /tickets — Support tickets',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getTickets()),
  },
  {
    id: 'user-trainer-application',
    name: 'GET /trainer/my-application — Trainer application status',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMyTrainerApplication()),
  },
  {
    id: 'user-notes',
    name: 'GET /notes/courses/:id — Course notes',
    group: 'User',
    requiresAuth: true,
    skip: true,
    skipReason: 'Requires valid course_id with notes',
    run: () => expectOk(getCourseNotes(1)),
  },
  {
    id: 'user-mentor-conversations',
    name: 'GET /mentor/conversations — List conversations',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMentorConversations()),
  },
  {
    id: 'user-mentor-gap-analysis',
    name: 'GET /mentor/gap-analysis — Career gap analysis',
    group: 'User',
    requiresAuth: true,
    run: () => expectOk(getMentorGapAnalysis()),
  },
  {
    id: 'user-mentor-goal',
    name: 'POST /mentor/goal — Update career goal',
    group: 'User',
    requiresAuth: true,
    run: () =>
      expectOk(
        updateMentorGoal({
          current_role: 'Integration Tester',
          target_role: 'AI Engineer',
          years_experience: 2,
          career_goal_summary: 'Testing the mentor goal endpoint',
        }),
      ),
  },
  {
    id: 'user-mentor-chat',
    name: 'POST /mentor/chat — AI career chat',
    group: 'User',
    requiresAuth: true,
    skip: true,
    skipReason: 'Requires valid OPENAI_API_KEY configured on backend',
    run: () => expectOk(mentorChat({ message: 'Hello mentor' })),
  },
];

// ───────────────────────────────────────────────────────────────
// TRAINER TESTS
// ───────────────────────────────────────────────────────────────
const trainerTests: TestCase[] = [
  {
    id: 'trainer-dashboard',
    name: 'GET /trainer/dashboard — Trainer dashboard',
    group: 'Trainer',
    requiresAuth: true,
    requiresTrainer: true,
    run: () => expectOk(getTrainerDashboard()),
  },
  {
    id: 'trainer-courses',
    name: 'GET /trainer/courses — Trainer courses',
    group: 'Trainer',
    requiresAuth: true,
    requiresTrainer: true,
    run: () => expectOk(getTrainerCourses()),
  },
  {
    id: 'trainer-students',
    name: 'GET /trainer/students — Trainer students',
    group: 'Trainer',
    requiresAuth: true,
    requiresTrainer: true,
    run: () => expectOk(getTrainerStudents()),
  },
  {
    id: 'trainer-earnings',
    name: 'GET /trainer/earnings — Earnings',
    group: 'Trainer',
    requiresAuth: true,
    requiresTrainer: true,
    run: () => expectOk(getTrainerEarnings()),
  },
  {
    id: 'trainer-payouts',
    name: 'GET /trainer/payouts — Payouts',
    group: 'Trainer',
    requiresAuth: true,
    requiresTrainer: true,
    run: () => expectOk(getTrainerPayouts()),
  },
];

// ───────────────────────────────────────────────────────────────
// ADMIN TESTS
// ───────────────────────────────────────────────────────────────
const adminTests: TestCase[] = [
  {
    id: 'admin-stats',
    name: 'GET /admin/stats — Dashboard stats',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminStats()),
  },
  {
    id: 'admin-users',
    name: 'GET /admin/users — User list',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminUsers()),
  },
  {
    id: 'admin-courses',
    name: 'GET /admin/courses — Course list',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminCourses()),
  },
  {
    id: 'admin-payments',
    name: 'GET /admin/payments — Payment list',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminPayments()),
  },
  {
    id: 'admin-enrollments',
    name: 'GET /admin/enrollments — Enrollment list',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminEnrollments()),
  },
  {
    id: 'admin-leads',
    name: 'GET /admin/leads — Lead list',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminLeads()),
  },
  {
    id: 'admin-categories',
    name: 'GET /admin/categories — Categories',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getCategories()),
  },
  {
    id: 'admin-trainers',
    name: 'GET /admin/trainers — Trainers',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getTrainers()),
  },
  {
    id: 'admin-cms',
    name: 'GET /admin/cms/home — CMS block',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getCms('home')),
  },
  {
    id: 'admin-trainer-applications',
    name: 'GET /admin/trainer-applications — Applications',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getTrainerApplications()),
  },
  {
    id: 'admin-pending-reviews',
    name: 'GET /admin/reviews/pending — Pending reviews',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getPendingReviews()),
  },
  {
    id: 'admin-content-flags',
    name: 'GET /admin/content-flags — Content flags',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getContentFlags()),
  },
  {
    id: 'admin-payouts',
    name: 'GET /admin/payouts — Payouts',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminPayouts()),
  },
  {
    id: 'admin-logs',
    name: 'GET /admin/logs — Error logs',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminLogs()),
  },
  {
    id: 'admin-logs-stats',
    name: 'GET /admin/logs/stats — Log stats',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminLogStats()),
  },
  {
    id: 'admin-marketing-overview',
    name: 'GET /admin/marketing/overview — Marketing overview',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getMarketingOverview()),
  },
  {
    id: 'admin-marketing-funnel',
    name: 'GET /admin/marketing/funnel — Marketing funnel',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getMarketingFunnel()),
  },
  {
    id: 'admin-marketing-channels',
    name: 'GET /admin/marketing/channels — Channels',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getMarketingChannels()),
  },
  {
    id: 'admin-marketing-campaigns',
    name: 'GET /admin/marketing/campaigns — Campaigns',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getMarketingCampaigns()),
  },
  {
    id: 'admin-marketing-coupons',
    name: 'GET /admin/marketing/coupons — Coupon analytics',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getMarketingCoupons()),
  },
  {
    id: 'admin-marketing-trends',
    name: 'GET /admin/marketing/trends — Trends',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getMarketingTrends()),
  },
  {
    id: 'admin-abandoned-checkouts',
    name: 'GET /admin/abandoned-checkouts — Abandoned checkouts',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () => expectOk(getAdminAbandonedCheckouts()),
  },
  {
    id: 'admin-settings-update',
    name: 'PUT /settings — Update system settings',
    group: 'Admin',
    requiresAuth: true,
    requiresAdmin: true,
    run: () =>
      expectOk(
        updateSystemSettings({
          settings: [
            { key: 'integration_test_timestamp', value: String(Date.now()), group_name: 'system', is_public: false },
          ],
        }),
      ),
  },
];

// ───────────────────────────────────────────────────────────────
// ALL TESTS
// ───────────────────────────────────────────────────────────────
export const allTests: TestCase[] = [
  ...publicTests,
  ...authTests,
  ...userTests,
  ...trainerTests,
  ...adminTests,
];

export const testGroups = Array.from(new Set(allTests.map((t) => t.group)));
